import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandLogo } from "@/components/BrandLogo";
import { Loader2 } from "lucide-react";

const LOGO = "https://customer-assets-4nw71qhi.emergentagent.net/job_escola-dados/artifacts/t4kwn57z_Logo%20Plataforma%20Acompanha%20Educacional.png";
const CLASSROOM = "https://images.unsplash.com/photo-1635424239131-32dc44986b56?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const DEMO = [
  { key: "direcao", label: "Direção", email: "direcao@dompedro.edu.br" },
  { key: "coordenacao", label: "Coordenação", email: "coordenacao@dompedro.edu.br" },
  { key: "professor", label: "Professor", email: "professor@dompedro.edu.br" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (d) => {
    setEmail(d.email);
    setPassword("Acompanha@2026");
  };

  return (
    <div className="min-h-screen w-full lg:grid lg:grid-cols-2">
      {/* Brand side — hero com sala de aula */}
      <div className="relative hidden flex-col items-center justify-center overflow-hidden p-12 lg:flex"
        style={{ backgroundImage: `url(${CLASSROOM})`, backgroundSize: "cover", backgroundPosition: "center" }}
        data-testid="login-hero">
        {/* sobreposição escura suave para legibilidade */}
        <div className="absolute inset-0 bg-slate-950/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/30 to-slate-950/50" />
        <div className="relative z-10 flex flex-col items-center text-center">
          <img src={LOGO} alt="ACOMPANHA — Plataforma de Acompanhamento Escolar"
            className="w-[420px] max-w-[82%] drop-shadow-2xl" data-testid="hero-logo" />
          <p className="mt-8 font-display text-3xl font-bold tracking-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)] xl:text-4xl">
            Cada presença conta. Cada aluno importa.
          </p>
        </div>
      </div>

      {/* Form side */}
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-5 py-10">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <BrandLogo />
          </div>
          <h2 className="font-display text-2xl font-bold text-slate-900">Entrar na plataforma</h2>
          <p className="mt-1 text-sm text-slate-500">Acesse com seu e-mail institucional e senha.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">E-mail</Label>
              <Input id="email" type="email" data-testid="login-email-input" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="seu@email.com" required autoComplete="email" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Senha</Label>
                <Link to="/forgot-password" className="text-xs font-medium text-blue-700 hover:underline" data-testid="login-forgot-link">
                  Esqueceu a senha?
                </Link>
              </div>
              <Input id="password" type="password" data-testid="login-password-input" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required autoComplete="current-password" />
            </div>

            {error && (
              <div data-testid="login-error" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="h-11 w-full bg-blue-700 text-base font-semibold hover:bg-blue-800">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Entrar"}
            </Button>
          </form>

          <div className="mt-8">
            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Contas de demonstração</p>
            <div className="grid grid-cols-3 gap-2">
              {DEMO.map((d) => (
                <button key={d.key} type="button" data-testid={`demo-role-${d.key}`} onClick={() => fillDemo(d)}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-xs font-semibold text-slate-700 transition hover:border-blue-400 hover:bg-blue-50">
                  {d.label}
                </button>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-slate-400">Senha demo: Acompanha@2026</p>
          </div>
        </div>
      </div>
    </div>
  );
}
